import { toGitLabGid } from "@gitlab-org/core";
import { UpdateToolCallApprovalsMutation, UpdateToolCallApprovalsWithSourceMutation } from "@gitlab-org/graphql";
import { McpToolName } from "@gitlab-org/ai-configuration";
//#region src/capability_checker.ts
const TOOL_APPROVAL_CAPABILITY = "tool_call_approval";
const TOOL_PATTERN_APPROVAL_CAPABILITY = "tool_call_pattern_approval";
const TOOL_APPROVAL_SOURCE_CAPABILITY = "tool_call_approval_source";
/**
* Checks if tool call approvals (session-scoped approvals) are supported.
*
* Checks if the Gateway advertises the 'tool_call_approval' capability, which indicates
* support for session-scoped tool approvals via GraphQL mutations.
*
* If the capability is not present, the function returns false and users will only
* see "Approve once" option. MCP tools may still use local controller fallback.
*
* The capabilities are retrieved from WorkflowRunner.getServerCapabilities(),
* which returns capabilities negotiated during the direct_access flow.
*
* @param options - Capability check options including workflow capabilities
* @returns true if Gateway supports tool call approvals, false otherwise
*/
function supportsToolCallApprovals(options) {
	const { logger, capabilities } = options;
	if (!capabilities) {
		logger.warn(`Server capabilities not available. This may indicate an issue with direct_access flow. Defaulting to no session approval support.`);
		return false;
	}
	if (!capabilities.includes(TOOL_APPROVAL_CAPABILITY)) {
		logger.warn(`Tool approval persistence unavailable: Gateway doesn't have '${TOOL_APPROVAL_CAPABILITY}' capability. This may indicate Gateway version mismatch or feature flag disabled.`);
		return false;
	}
	logger.debug(`Tool approval persistence available`);
	return true;
}
/**
* Checks if pattern-based tool call approvals are supported.
* Pattern approvals are an extension of session approvals — both are gated on the same backend setting.
*/
function supportsPatternApprovals(options) {
	const { logger, capabilities } = options;
	if (!capabilities) {
		logger.warn(`Server capabilities not available. This may indicate an issue with direct_access flow. Defaulting to no pattern approval support.`);
		return false;
	}
	if (!capabilities.includes(TOOL_PATTERN_APPROVAL_CAPABILITY)) {
		logger.debug(`Pattern approval unavailable: Gateway doesn't have '${TOOL_PATTERN_APPROVAL_CAPABILITY}' capability.`);
		return false;
	}
	logger.debug(`Pattern approval available`);
	return true;
}
/**
* Checks if the instance supports recording the approval's source (audit provenance)
* on the tool call approvals mutation.
*
* The `tool_call_approval_source` capability is minted by Rails itself (not DWS) starting
* with GitLab 19.3.0, the release that ships the `DuoWorkflowToolCallApprovalSource` enum
* and the `approvalSource` mutation argument (gitlab-org/gitlab!247456). Its presence means
* the `UpdateToolCallApprovalsWithSourceMutation` document validates against the instance
* schema; its absence means the legacy document must be used, because referencing the enum
* type fails whole-document GraphQL validation on older instances.
*
* Unlike `supportsToolCallApprovals`, this check never warns: missing capabilities only
* degrade the audit annotation, not the approval persistence itself.
*/
function supportsApprovalSource(options) {
	const { logger, capabilities } = options;
	if (!capabilities) {
		logger.debug(`Server capabilities not available; assuming no '${TOOL_APPROVAL_SOURCE_CAPABILITY}' support.`);
		return false;
	}
	const instanceSupports = capabilities.includes(TOOL_APPROVAL_SOURCE_CAPABILITY);
	logger.debug(instanceSupports ? `Approval source recording available` : `Approval source recording unavailable: instance doesn't advertise '${TOOL_APPROVAL_SOURCE_CAPABILITY}' capability (GitLab 19.3.0+).`);
	return instanceSupports;
}
//#endregion
//#region src/persistence.ts
const WORKFLOW_GID_TYPE = "Ai::DuoWorkflows::Workflow";
/**
* Selects the mutation document and the `approvalSource` variable based on the
* `tool_call_approval_source` server capability. On instances without it, the legacy
* document is used and the variable is dropped. See `supportsApprovalSource` in
* `capability_checker.ts` for why the capability gates document selection.
*/
function selectMutationForApprovalSource(logger, capabilities, approvalSource) {
	if (supportsApprovalSource({
		logger,
		capabilities
	})) return {
		query: UpdateToolCallApprovalsWithSourceMutation.query,
		approvalSource
	};
	if (approvalSource) logger.debug(`Dropping approvalSource "${approvalSource}" from tool call approval persistence: instance does not advertise the 'tool_call_approval_source' capability (GitLab 19.3.0+). The approval itself is still persisted.`);
	return {
		query: UpdateToolCallApprovalsMutation.query,
		approvalSource: void 0
	};
}
/**
* Persists a tool approval for the current workflow session.
*
* Strategy:
* 1. Check if tool call approvals are supported (Gateway has 'tool_call_approval' capability)
* 2. If supported → Use GraphQL mutation (all tools, including MCP)
* 3. If not supported AND tool is MCP → Use local MCP controller (backward compat)
* 4. If not supported AND tool is non-MCP → Skip (user sees approval but it's not persisted)
*
* All errors are logged internally and do not propagate to the caller.
*/
async function persistToolApprovalForSession(options) {
	const { workflowId, toolName, toolArgs, gitlabApiService, logger, capabilities, mcpToolApprovalController, approvalSource } = options;
	if (supportsToolCallApprovals({
		logger,
		capabilities
	})) {
		await persistViaGraphQL(workflowId, toolName, toolArgs, gitlabApiService, logger, capabilities, approvalSource);
		return;
	}
	if (McpToolName.is(toolName)) {
		if (!mcpToolApprovalController) {
			logger.warn(`Cannot persist session approval for MCP tool "${toolName}": MCP controller not available`);
			return;
		}
		await persistViaMcpController(workflowId, toolName, mcpToolApprovalController, logger);
		return;
	}
	logger.info(`Session approval for "${toolName}" cannot be persisted: Gateway does not support GraphQL approvals for non-MCP tools. Approval is effective for this single tool call only.`);
}
/**
* Persists tool approval via GraphQL mutation
*/
async function persistViaGraphQL(workflowId, toolName, toolArgs, gitlabApiService, logger, capabilities, approvalSource) {
	try {
		const graphqlWorkflowId = toGitLabGid(WORKFLOW_GID_TYPE, workflowId);
		const selected = selectMutationForApprovalSource(logger, capabilities, approvalSource);
		const variables = {
			workflowId: graphqlWorkflowId,
			toolName,
			toolCallArgs: JSON.stringify(toolArgs),
			approvalSource: selected.approvalSource
		};
		logger.info(`Persisting tool approval for "${toolName}" via GraphQL (workflow: ${workflowId}, gid: ${graphqlWorkflowId}, approvalSource: ${selected.approvalSource ?? "(not provided)"})`);
		logger.debug(`GraphQL mutation variables: ${JSON.stringify(variables)}`);
		const result = await gitlabApiService.fetchFromApi({
			type: "graphql",
			query: selected.query,
			variables
		});
		logger.debug(`GraphQL mutation response: ${JSON.stringify(result.updateDuoWorkflowToolCallApprovals)}`);
		if (result.updateDuoWorkflowToolCallApprovals.errors.length > 0) {
			logger.warn(`Failed to persist tool approval: ${result.updateDuoWorkflowToolCallApprovals.errors.join(", ")}`);
			return;
		}
		logger.info(`Tool approval persisted successfully for "${toolName}". Args: ${JSON.stringify(toolArgs)}`);
	} catch (error) {
		logger.error(`Error persisting tool approval via GraphQL: ${String(error)}`);
	}
}
/**
* Persists a pattern-based tool approval for the current workflow session.
*
* All errors are logged internally and do not propagate to the caller.
* Callers are expected to gate on `supportsPatternApprovals` before invoking this function.
*/
async function persistPatternApprovalForSession(options) {
	const { workflowId, toolName, pattern, gitlabApiService, logger, capabilities, approvalSource } = options;
	try {
		const graphqlWorkflowId = toGitLabGid(WORKFLOW_GID_TYPE, workflowId);
		const selected = selectMutationForApprovalSource(logger, capabilities, approvalSource);
		const variables = {
			workflowId: graphqlWorkflowId,
			toolName,
			pattern,
			approvalSource: selected.approvalSource
		};
		logger.info(`Persisting pattern approval for "${toolName}" via GraphQL (workflow: ${workflowId}, pattern: "${pattern}", approvalSource: ${selected.approvalSource ?? "(not provided)"})`);
		logger.debug(`GraphQL mutation variables: ${JSON.stringify(variables)}`);
		const result = await gitlabApiService.fetchFromApi({
			type: "graphql",
			query: selected.query,
			variables
		});
		logger.debug(`GraphQL mutation response: ${JSON.stringify(result.updateDuoWorkflowToolCallApprovals)}`);
		if (result.updateDuoWorkflowToolCallApprovals.errors.length > 0) {
			logger.warn(`Failed to persist pattern approval: ${result.updateDuoWorkflowToolCallApprovals.errors.join(", ")}`);
			return;
		}
		logger.info(`Pattern approval persisted successfully for "${toolName}". Pattern: "${pattern}"`);
	} catch (error) {
		logger.error(`Error persisting pattern approval via GraphQL: ${String(error)}`);
	}
}
/**
* Persists MCP tool approval via local controller (legacy fallback when Gateway doesn't support tool_call_approval)
*/
async function persistViaMcpController(workflowId, toolName, mcpToolApprovalController, logger) {
	try {
		logger.info(`Persisting MCP tool approval for "${toolName}" via local controller (legacy fallback)`);
		await mcpToolApprovalController.approveToolForSession(workflowId, toolName);
		logger.info(`MCP tool approval persisted successfully for "${toolName}"`);
	} catch (error) {
		logger.error(`Error persisting MCP tool approval via local controller: ${String(error)}`);
	}
}
//#endregion
export { TOOL_APPROVAL_SOURCE_CAPABILITY, persistPatternApprovalForSession, persistToolApprovalForSession, supportsApprovalSource, supportsPatternApprovals, supportsToolCallApprovals };

//# sourceMappingURL=index.mjs.map